import { PageViewEventLogger } from '@repo/tracking-service';
import { AuthGuard } from '@repo/user-session';
import { GetServerSideProps } from 'next';
import { useMemo, useState } from 'react';

import Layout from '@/components/layout/Layout';
import { ApplyResultContent } from '@/components/tax-refund/apply/result/ApplyResultContent';
import TritxApplyRefundResultContent from '@/components/tax-refund/apply/result/TritxApplyRefundResultContent';
import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { useRightInquiryTextButton } from '@/components/tax-refund/common/hooks/use-create-header-props';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { BusinessType, RefundStatusType } from '@/lib/types/request';

const ApplyResultPage = ({ businessType, taxType }: { businessType: BusinessType; taxType: 'tritx' | 'income' | '' }) => {
  const [channelTalkLink, setChannelTalkLink] = useState(SUPPORT_BOT_LINK.default);
  const rightInquiryButton = useRightInquiryTextButton(channelTalkLink, 'indis-result_cx_top-right');

  const eventProperty = useMemo(() => {
    if (businessType !== 'indis') return undefined;

    const indis = getIndisSearchRefundData();
    const refundStatus = taxType === 'tritx' ? indis?.tritxRefundInfo?.refundStatus : indis?.refundStatus;
    const refundStage = taxType === 'tritx' ? indis?.tritxRefundInfo?.refundStage : indis?.refundStage;
    const cancelStage = taxType === 'tritx' ? indis?.tritxRefundInfo?.cancelStage : indis?.cancelStage;

    return { status: getRefundStatus(refundStatus, refundStage, cancelStage), taxType: taxType === 'tritx' ? 'capital-gain' : 'global-income' };
  }, [businessType, taxType]);

  return (
    <AuthGuard>
      <Layout
        leftIcon={{ icon: 'back', clickEvent: { name: businessType === 'indis' ? 'indis-result_topnav_back' : 'corps-result_topnav_back' } }}
        rightTextButton={rightInquiryButton}
      >
        {taxType === 'tritx' ? (
          <TritxApplyRefundResultContent />
        ) : (
          <ApplyResultContent businessType={businessType} channelTalkLink={channelTalkLink} setChannelTalkLink={setChannelTalkLink} />
        )}
      </Layout>
      <PageViewEventLogger pageName={businessType === 'indis' ? 'indis-result' : 'corps-result'} eventProperty={eventProperty} />
    </AuthGuard>
  );
};

export const getServerSideProps = (async ({ query }) => {
  if (query?.type) {
    const businessType: BusinessType = query?.type === 'corps' ? 'corps' : 'indis';
    const taxType = query?.type.includes('tritx') ? 'tritx' : 'income';
    return { props: { businessType, taxType } };
  }
  return { props: { businessType: '', taxType: '' } };
}) satisfies GetServerSideProps;

export default ApplyResultPage;

const getRefundStatus = (refundStatus?: string | null, refundStage?: string | null, cancelStage?: string | null): RefundStatusType | undefined => {
  if (!refundStatus) return;

  const statusMap: Record<string, RefundStatusType> = {
    RefundFinish: 'paid',
    RefundImpossible: 'impossible'
  };

  if (statusMap[refundStatus]) return statusMap[refundStatus];

  if (refundStatus === 'ApplyComplete') {
    if (cancelStage === 'Canceling') return 'cancel';

    const stageMap: Record<string, RefundStatusType> = {
      Review: 'review',
      Declare: 'declare',
      RefundPreDetermine: 'predetermine',
      RefundDetermine: 'determine',
      RefundCompleteA: 'refunded',
      RefundCompleteB: 'refunded'
    };

    return stageMap[refundStage ?? ''] ?? 'applied';
  }

  return;
};
